// Хуки для работы с recviz через TanStack Query
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as recvizApi from '@/api/recviz'
import { useOrgScopedQueryKey } from '@/hooks/useOrgScopedQueryKey'
import { getApiErrorMessage } from '@/utils/apiError'

// Список периодов
export const useRecvizPeriods = () => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['recviz-periods', orgContextKey],
    queryFn: recvizApi.fetchRecvizPeriods,
  })
}

// Данные отделений с фильтрами
export const useRecvizData = (
  period: string,
  filters?: recvizApi.RecvizDataFilters
) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['recviz-data', orgContextKey, period, filters],
    queryFn: () => recvizApi.fetchRecvizData(period, filters),
    enabled: !!period,
  })
}

// Сводная информация
export const useRecvizSummary = (period: string) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['recviz-summary', orgContextKey, period],
    queryFn: () => recvizApi.fetchRecvizSummary(period),
    enabled: !!period,
  })
}

// Обновление данных отделений (включая manual_* поля для B.3)
export const useUpdateRecvizData = (period: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updates: Record<string, recvizApi.RecvizDepartmentUpdate>) =>
      recvizApi.updateRecvizData(period, updates),
    onSuccess: (result) => {
      if (result.errors.length > 0) {
        message.warning(`Обновлено: ${result.updated_count}, ошибок: ${result.errors.length}`)
      } else {
        message.success(`Сохранено: ${result.updated_count} записей`)
      }
      // Инвалидируем кэш данных и сводки
      queryClient.invalidateQueries({ queryKey: ['recviz-data', period] })
      queryClient.invalidateQueries({ queryKey: ['recviz-summary', period] })
    },
    onError: () => {
      message.error('Ошибка сохранения')
    },
  })
}

// B.3 Hooks для вычисляемых колонок

// Получение детализации расчёта показателя
export const useCalculationDetail = (
  departmentId: string,
  indicatorType: recvizApi.IndicatorType,
  year: number,
  enabled: boolean = true
) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['recviz-calculation-detail', orgContextKey, departmentId, indicatorType, year],
    queryFn: () => recvizApi.fetchCalculationDetail(departmentId, indicatorType, year),
    enabled: enabled && !!departmentId && !!indicatorType && !!year,
  })
}

// Переключение включения периода в расчёт
export const useTogglePeriod = (
  departmentId: string,
  indicatorType: recvizApi.IndicatorType,
  year: number
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (periodData: recvizApi.PeriodToggleRequest) =>
      recvizApi.togglePeriodInclusion(departmentId, indicatorType, year, periodData),
    onSuccess: () => {
      message.success('Настройки периода обновлены')
      // Инвалидируем кэш детализации для пересчёта
      queryClient.invalidateQueries({
        queryKey: ['recviz-calculation-detail', departmentId, indicatorType, year],
      })
      // Также инвалидируем основные данные отделения
      queryClient.invalidateQueries({ queryKey: ['recviz-data'] })
    },
    onError: () => {
      message.error('Ошибка обновления настроек периода')
    },
  })
}

// Получение настроек порогов светофора
export const useThresholdSettings = () => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['recviz-threshold-settings', orgContextKey],
    queryFn: recvizApi.fetchThresholdSettings,
  })
}

// Обновление manual value для показателя
export const useUpdateManualValue = (period: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: recvizApi.ManualValueRequest) =>
      recvizApi.updateManualValue(request),
    onSuccess: (result) => {
      if (result.status === 'updated') {
        message.success('Значение сохранено')
      } else if (result.status === 'deleted') {
        message.success('Значение сброшено')
      }
      // Инвалидируем кэш данных отделений
      queryClient.invalidateQueries({ queryKey: ['recviz-data', period] })
    },
    onError: () => {
      message.error('Ошибка сохранения значения')
    },
  })
}

// 4.5.2 Формирование справочника реквизитов
export const useGenerateRecviz = () => {
  return useMutation({
    mutationFn: (request: recvizApi.GenerateRecvizRequest) =>
      recvizApi.generateRecviz(request),
    onSuccess: (result) => {
      if (result.export_status?.success) {
        message.success(`Справочник сформирован: ${result.rows_count} записей. Экспортировано в ClickHouse: ${result.export_status.rows_exported}`)
      } else if (result.export_status?.error) {
        message.warning(`Справочник сформирован: ${result.rows_count} записей. Ошибка экспорта: ${result.export_status.error}`)
      } else {
        message.success(`Справочник сформирован: ${result.rows_count} записей`)
      }
    },
    onError: (error: unknown) => {
      message.error(`Ошибка формирования справочника: ${getApiErrorMessage(error, 'Неизвестная ошибка')}`)
    },
  })
}

// Multi-period generation
export const useGenerateRecvizMulti = () => {
  return useMutation({
    mutationFn: (request: recvizApi.GenerateRecvizMultiRequest) =>
      recvizApi.generateRecvizMulti(request),
    onSuccess: (result) => {
      const exported = result.results.filter(r => r.export_status?.success).length
      if (exported > 0) {
        message.success(
          `Сформировано: ${result.total_rows} записей за ${result.results.length} периодов. ` +
          `Экспорт: ${exported} периодов.`
        )
      } else {
        message.success(`Сформировано: ${result.total_rows} записей за ${result.results.length} периодов`)
      }
    },
    onError: (error: unknown) => {
      message.error(`Ошибка формирования: ${getApiErrorMessage(error, 'Неизвестная ошибка')}`)
    },
  })
}
